/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import {
  Divider,
  Typography,
  Button,
  MenuItem,
  TextField,
  CircularProgress,
  Grid,
  Container,
  Tab,
  Tabs,
  Box
} from "@mui/material";
import DownArrow from '@mui/icons-material/KeyboardArrowDown';
import UpArrow from '@mui/icons-material/KeyboardArrowUp';
import CachedIcon from '@mui/icons-material/Cached';
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CustomEditor } from "./CustomEditor/CustomEditor";
import useNotes from "./CustomEditor/useNotes";
import { useExpectedResponse } from "hooks/useExpectedResponses";
import { v4 as uuid } from 'uuid';
import { ResultsProps } from "interfaces/pages/components";
import { ChriRetrievalState } from "interfaces/pages/components/ChriRecordProps";
import { useUserStore } from "../../../../../state";
import { extractPageName } from "common/CustomTable/helpers";
import { TableNames } from "customEnums/TableNames";
import { OutgoingTransmissionTable } from "./OutgoingTransmissionTable"
import { IncomingResponseTable } from "./IncomingResponseTable"
import { useMugshots } from "hooks/useMugshots";
import ChriRecord from './components/ChriRecord';
import { useChriResults } from "hooks/useChriResults";
import { InlineAlert } from "common/alerts";
import { AlertColor } from "../../../../../customEnums/AlertColor";
import { formatPlainTextForEditor } from "utils/printText";

const Results: React.FC<ResultsProps> = ({
  userRole,
  results,
  rapSheets,
  transactionalInfo,
  resultsResponses,
  tabValue,
  setEditRedactedMode,
  editRedactedMode,
  setTextChanges,
  textChanges,
  responseAssociationData,
  responseApplicantSid
}) => {

  const isHtmlString = (str: string): boolean => {
    return /<HTML|<displayHtml/i.test(str);
  };

  const originalText = useMemo(() => {
    if (!rapSheets?.length) return "";

    return rapSheets.map((str: string, index: number) => {
      const isHtml = isHtmlString(str);
      const formattedSheet = formatPlainTextForEditor(str, isHtml);

      const pageBreakStyle = index > 0 ? 'page-break-before: always;' : '';
      const separator = index > 0
        ? `<div style="margin-bottom: 20px;"><strong>End of sheet ${index}</strong><br />-----------------------------------------------------------------------------------</div>`
        : '';

      return `
        ${separator}
        <div style="${pageBreakStyle}">
          ${formattedSheet}
        </div>
      `;
    }).join("");
  }, [rapSheets]);

  const { notesJSON, fetchStatus } = useNotes("redact");
  const [isVisible, setIsVisible] = useState(true);
  const [rapSheetVisible, setRapSheetVisible] = useState(true);
  const [chriRecordVisible, setChriRecordVisible] = useState(true);
  const [mugshotsVisible, setMugshotsVisible] = useState(true);
  const userConfig = useUserStore(state => state.userConfig);
  const { usersPermissions } = userConfig;
  const [view, setView] = useState(usersPermissions?.["viewOriginalResults"] ? 'Original' : 'Redacted');
  const [redactedTextCreation, setRedactionTextCreation] = useState<string>('');
  const [createdRedaction, setCreatedRedaction] = useState<boolean>(false);
  const [tableData, setTableData] = useState<Array<Object>>(results);
  const [resultRespData, setResultRespData] = useState<Array<Object>>(resultsResponses)
  const locationObj = useLocation()
  const pageName = extractPageName(locationObj.pathname)
  const config = useUserStore(state => state.config)
  const [tabSelectedIndex, setTabSelectedIndex] = useState(0);
  const displayMugShots = config.pages[pageName]["responseData"]["displayMugshots"];
  const { applicantImageUrls } = useMugshots(responseAssociationData, displayMugShots);
  const featureFlags = useUserStore(state => state.featureFlags);
  const {
    chriResults,
    chriRetrievalState,
    chriRecordView,
    chriTextChanges,
    existChriRedaction,
    setChriRetrievalState,
    setChriRecordView,
    setChriTextChanges,
    handleRetrieveChri,
    saveRapsheetRedaction
  } = useChriResults(transactionalInfo.transactionNumber, responseApplicantSid, userConfig);

  console.log("displayMugShots", displayMugShots)

  useEffect(() => {
    console.log('Add redaction has been activated:', editRedactedMode)
  }, [editRedactedMode]);

  const decodeRedactedText = (text: any): string => {
    if (!text) return "";
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const decodedText = doc.documentElement.textContent ?? "";
    return decodedText
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#x([0-9A-Fa-f]+);/g, (_match: any, hex: any) => String.fromCharCode(parseInt(hex, 16)));
  }

  const redactedText = decodeRedactedText(notesJSON?.[0]?.body)

  const changeView = (e: any) => {
    setView(e.target.value)
  }

  const changeChriRecordView = (e: any) => {
    setChriRecordView(e.target.value);
  }

  const toggleDataVisibility = () => {
    setRapSheetVisible(!rapSheetVisible);
    setChriRecordVisible(!chriRecordVisible);
    setMugshotsVisible(!mugshotsVisible);
  }

  const toggleVisibility = () => {
    setIsVisible(!isVisible)
  }

  useEffect(() => {
    if (resultsResponses?.length !== 0) {
      console.log('CustomTableFooter onRowsPerPageChange resultResponses non sorted: ', resultsResponses)
      const sortResultRespData = [...resultsResponses].sort((a: any, b: any) => {
        if (a.type1Field7.toLowerCase() === 'state') return -1;
        if (b.type1Field7.toLowerCase() === 'state') return 1;
        return 0
      })
      console.log('CustomTableFooter onRowsPerPageChange resultResponses sorted: ', sortResultRespData)
      setResultRespData(sortResultRespData)
    }
  }, [resultsResponses])

  useEffect(() => {
    const sortTableData = [...results].sort((a: any, b: any) => {
      if (a.agency.toLowerCase() === 'state') return -1;
      if (b.agency.toLowerCase() === 'state') return 1;
      return 0
    })
    setTableData(sortTableData);
  }, [results, userRole]);

  type MyParams = {
    registrationId: string;
  };
  const { registrationId } = useParams<keyof MyParams>() as unknown as MyParams;
  const { expectedResponse, receivedResponses } = useExpectedResponse(registrationId, resultRespData);
  const allResponsesReceived = expectedResponse === receivedResponses;
  const isLoading = fetchStatus === "loading" || fetchStatus === "idle";

  const notesLoadingContainer = {
    display: 'flex',
    padding: '1.5rem',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    alignSelf: 'stretch',
    backgroundColor: 'common.black'
  }

  if (isLoading) {
    return (
      <Grid sx={notesLoadingContainer}>
        <CircularProgress />
      </Grid>
    );
  }

  const handleResponseType = (type: string) => {
    switch (type.toLowerCase()) {
      case 'i':
      case 'y':
        return 'Hit';
      case 'n':
        return 'No Hit';
      case 'errt':
        return 'ERRT-Error';
      case 'qc_reject':
        return 'QC Reject';
      case 'ack':
        return 'Acknowledgement';
      default:
        return type;
    }
  }

  let newResultRespData = resultRespData.map((v: any) => {
    console.log('CustomTableFooter newResultResponses: ', {
      ...v,
      uuid: uuid(),
      type2Field59: handleResponseType(v.type2Field59)
    })
    return {
      ...v,
      uuid: uuid(),
      type2Field59: handleResponseType(v.type2Field59)
    }
  }
  )

  const canViewRedactedResult = usersPermissions?.["viewRedactedResults"];
  const canEditRedactedResults = usersPermissions?.["editRedactedResults"];

  const resultsBodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1.5rem',
    backgroundColor: 'common.black',
    height: 'auto'
  }
  const infoAlertStyle = {
    backgroundColor: 'info.main',
    border: '1px solid',
    borderColor: 'info.dark',
    width: '100%',
    '.MuiAlert-icon': {
      color: 'info.contrastText'
    }
  }
  const successAlertStyle = {
    width: '100%',
    border: '1px solid',
    borderColor: 'success.dark',
    backgroundColor: 'success.main',
    '.MuiAlert-icon': {
      color: 'success.contrastText'
    }
  }

  const renderTabs = (items: any[]) => {
    if (!featureFlags["resultschrirecord-showhide"] || !usersPermissions?.["viewChriTab"]) {
      items = removeByKey(items, 'CHRI RECORD');
    }

    if (!displayMugShots) {
      return removeByKey(items, 'MUGSHOTS').map((item) => <Tab key={item.key} label={item.label} />);
    } else {
      return items.map((item) => {
        let label;
        if (item.label === 'MUGSHOTS' && applicantImageUrls && applicantImageUrls.length) {
          label = `${item.label} (${applicantImageUrls.length})`
        } else {
          label = item.label;
        }
        return <Tab key={item.key} label={label} />
      });
    }
  }

  function removeByKey<T extends { key: string }>(arr: T[], key: string): T[] {
    return arr.filter(item => item.key !== key);
  }

  const renderImages = (mugshotArray: { id: string; urls: string[]; }[]) => {
    if (mugshotArray && mugshotArray.length) {
      return mugshotArray.map((mugshot: { id: string, urls: string[] }, index) => {
        return <img key={mugshot.id} style={{ height: "100%", width: "100%", margin: '0 8px' }}
          src={mugshot.urls[0]}
          alt='mugshot'
          loading="lazy"
        />
      })
    } else {
      return <div>NO DATA</div>
    }
  }

  console.log('canViewRedactedResult', canViewRedactedResult)

  let items = [
    {
      label: 'RESPONSE DATA',
      key: 'RESPONSE DATA',
      children: (usersPermissions?.["viewOriginalResults"] || canViewRedactedResult) &&
        <>
          {rapSheetVisible ? (
            <>
              {(usersPermissions?.["viewOriginalResults"] && view === 'Original') || (canViewRedactedResult && view === 'Redacted') ? (
                <CustomEditor
                  isEditable={false}
                  tcn={transactionalInfo.transactionNumber}
                  redactedJSON={notesJSON}
                  view={view}
                  setView={setView}
                  textChanges={textChanges}
                  registrationId={registrationId}
                  viewOriginal={originalText}
                  originalText={view === 'Original' ? originalText : redactedTextCreation ? redactedTextCreation : redactedText || originalText}
                  {...(view === 'Original' && {
                    title: "ORIGINAL",
                    setEditRedactedMode: setEditRedactedMode,
                    redactedTextCreation: redactedTextCreation,
                    createdRedaction: createdRedaction,
                    allResponsesRecieved: expectedResponse && expectedResponse > 0 ? expectedResponse === receivedResponses : false,
                  })}
                  {...(view === 'Redacted' && {
                    title: "REDACTED",
                    redactedText: redactedText,
                    redactedTextCreation: redactedTextCreation,
                    createdRedaction: createdRedaction,
                  })}
                />
              ) : null}

              {canViewRedactedResult && usersPermissions?.["viewOriginalResults"] && view === 'Original & Redacted' ?
                <CustomEditor
                  isEditable={canEditRedactedResults && editRedactedMode}
                  setEditRedactedMode={setEditRedactedMode}
                  editRedactedMode={editRedactedMode}
                  viewOriginal={originalText}
                  originalText={redactedTextCreation ? redactedTextCreation : redactedText ? redactedText : originalText}
                  redactedJSON={notesJSON}
                  tcn={transactionalInfo.transactionNumber}
                  view={view}
                  setView={setView}
                  setRedactionTextCreation={setRedactionTextCreation}
                  setCreatedRedaction={setCreatedRedaction}
                  createdRedaction={createdRedaction}
                  allResponsesRecieved={expectedResponse && expectedResponse > 0 ? expectedResponse === receivedResponses : false}
                  setTextChanges={setTextChanges}
                  textChanges={textChanges}
                  tabValue={tabValue}
                  redactedText={redactedText}
                  registrationId={registrationId}
                />
                : null
              }
            </>)
            : null
          }
        </>
    },
    {
      label: 'CHRI RECORD',
      key: 'CHRI RECORD',
      children: usersPermissions?.["viewOriginalChri"] &&
        <>
          {chriRecordVisible ? (
            <>
              <ChriRecord
                chriResult={chriResults}
                transactionalInfo={transactionalInfo}
                setEditRedactedMode={setEditRedactedMode}
                editRedactedMode={editRedactedMode}
                setTextChanges={setChriTextChanges}
                textChanges={chriTextChanges}
                chriRetrievalState={chriRetrievalState}
                setChriRetrievalState={setChriRetrievalState}
                view={chriRecordView}
                setView={setChriRecordView}
                saveRapsheetRedaction={saveRapsheetRedaction}
              />
            </>)
            : null
          }
        </>
    },
    {
      label: 'MUGSHOTS',
      key: 'MUGSHOTS',
      children: usersPermissions?.["viewOriginalResults"] &&
        <>
          {mugshotsVisible ? (
            <>
              <Divider style={{ background: 'rgba(255, 255, 255, 0.12)', width: '100%', marginTop: '1rem', marginBottom: '1rem' }} variant="fullWidth" aria-hidden role="separator" />
              <Grid sx={{ height: 'auto', width: '100%', p: 0 }} >
                <Grid display='flex' justifyContent='space-between' width='100%' alignItems='center'>
                  <Box display='flex' justifyContent='space-between' sx={{ minWidth: '175px', height: '220px' }}>
                    {renderImages(applicantImageUrls)}
                  </Box>
                </Grid>
              </Grid>
            </>)
            : null
          }
        </>
    }
  ];

  const handleChange = (_event?: any, newValue?: any) => {
    if (editRedactedMode && textChanges) return;

    setEditRedactedMode(false);
    setTabSelectedIndex(newValue);
  };

  const disableRetrieveChriButton = () => {
    if (chriRetrievalState === ChriRetrievalState.LOADING) {
      return true;
    }
    return false;
  }

  return (
    <>
      <Container sx={resultsBodyStyle} disableGutters={true} maxWidth={false} data-testid="results-container" >
        <Grid sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }} data-testid="results-accordion">
          <Typography variant='h6' color='text.primary'>RESULTS</Typography>
          <Button size='small' endIcon={isVisible ? <DownArrow style={{ color: '#8A72FF' }} /> : <UpArrow style={{ color: '#8A72FF' }} />}
            disableRipple onClick={toggleVisibility} sx={{ padding: '4px 5px' }}>
            {isVisible ? 'HIDE' : 'SHOW'}
          </Button>
        </Grid>
        {isVisible ? (
          <>
            <Typography variant='body1' color='text.primary' >
              Review all outgoing transmissions and incoming responses.
              {(usersPermissions?.["createNewRedactedResults"] || usersPermissions?.["editRedactedResults"]) &&
                ' You may redact response data when all AFIS responses are received.'
              }
            </Typography>
            <Typography variant='subtitle2' color='text.primary'>OUTGOING TRANSMISSION</Typography>
            <OutgoingTransmissionTable
              totalRecords={tableData.length}
              name={TableNames.OUTGOING_TRANSMISSION}
              tableData={tableData} />

            <Typography variant='subtitle2' color='text.primary' sx={{ marginTop: '1rem' }}>INCOMING RESPONSE</Typography>

            {expectedResponse &&
              <InlineAlert
                severity={!allResponsesReceived ? AlertColor.INFO : AlertColor.SUCCESS}
                title={allResponsesReceived ? 'All responses received.' : 'Responses pending.'}
                message={`${receivedResponses ?? 0} of ${expectedResponse ?? 0} responses received.${!allResponsesReceived && usersPermissions?.["createNewRedactedResults"] ? ' Please wait on the remaining results to redact response information.' : ''}`}
                sx={allResponsesReceived ? successAlertStyle : infoAlertStyle}
              />
            }

            <IncomingResponseTable tableData={newResultRespData?.length ? newResultRespData : [{ type2Field59: "NO DATA" }]} totalRecords={newResultRespData.length} name={TableNames.INCOMING_RESPONSE} />


          </>
        ) : null}
        <Divider style={{ background: 'rgba(255, 255, 255, 0.12)', width: '100%', marginTop: '1rem', marginBottom: '1rem' }} variant="fullWidth" aria-hidden role="separator" />
        <Grid sx={{ height: 'auto', width: '100%', p: 0 }}>
          <Grid display='flex' justifyContent='space-between' width='100%' alignItems='center' data-testid="response-data-accordion">
            <Tabs value={tabSelectedIndex} onChange={handleChange} sx={{ bgcolor: 'white', padding: '0.5rem 1rem', backgroundColor: 'common.black' }} >
              {renderTabs(items)}
            </Tabs>
            <Grid width={tabSelectedIndex === 1 ? '32rem' : '15rem'} justifyContent={usersPermissions?.["viewRedactedChri"] ? 'space-between' : 'flex-end'} display='flex' gap='1.5rem'>
              {tabSelectedIndex === 1 &&
                <>
                  <Grid width='200px' item display='flex' justifyContent='center' alignItems='center'>
                    <Button
                      startIcon={chriRetrievalState !== ChriRetrievalState.INITIAL ? <CachedIcon /> : ''}
                      variant="contained"
                      onClick={handleRetrieveChri}
                      disabled={disableRetrieveChriButton()}
                      sx={{ padding: '8px 22px', alignItems: 'center', width: 'auto' }}>
                      RETRIEVE CHRI
                    </Button>
                  </Grid>

                  <TextField
                    sx={{ width: '14rem' }}
                    select
                    label="Select View"
                    defaultValue={'Original'}
                    value={chriRecordView}
                    data-testid="chri-select-view-text-field"
                    disabled={existChriRedaction ? ((usersPermissions?.["viewRedactedChri"]) ? false : true) : true}
                    SelectProps={{
                      sx: {
                        fontFamily: 'Montserrat',
                        '&.MuiMenu-paper': {
                          backgroundColor: 'red'
                        }
                      },
                    }}
                    onChange={changeChriRecordView}
                  >
                    <MenuItem value={'Original'} data-testid="chri-original-menu-item">Original</MenuItem>
                    <MenuItem value={'Redacted'} data-testid="chri-redacted-menu-item">Redacted</MenuItem>
                    <MenuItem value={'Original & Redacted'} data-testid="chri-original-redacted-menu-item">Original & Redacted</MenuItem>

                  </TextField>
                </>
              }
              {tabSelectedIndex !== 1 && usersPermissions?.["viewRedactedResults"] && usersPermissions?.["viewOriginalResults"] &&
                <TextField
                  sx={{ width: '14rem' }}
                  select
                  label="Select View"
                  defaultValue={'Original'}
                  value={view}
                  data-testid="select-view-text-field"
                  disabled={(usersPermissions?.["viewRedactedResults"] && (notesJSON.length !== 0 || createdRedaction)) && !editRedactedMode ? false : true}
                  SelectProps={{
                    sx: {
                      fontFamily: 'Montserrat',
                      '&.MuiMenu-paper': {
                        backgroundColor: 'red'
                      }
                    },
                  }}
                  onChange={changeView}
                >
                  <MenuItem value={'Original'} data-testid="original-menu-item">Original</MenuItem>
                  <MenuItem value={'Redacted'} data-testid="redacted-menu-item">Redacted</MenuItem>
                  <MenuItem value={'Original & Redacted'} data-testid="original-redacted-menu-item">Original & Redacted</MenuItem>
                </TextField>
              }
              <Grid item display='flex' justifyContent='center' alignItems='center'>
                <Button size='small' endIcon={isVisible ? <DownArrow style={{ color: '#8A72FF' }} /> : <UpArrow style={{ color: '#8A72FF' }} />}
                  disableRipple onClick={toggleDataVisibility} sx={{ padding: '4px 5px', height: '30.75px', alignItems: 'center' }}>
                  {rapSheetVisible ? 'HIDE' : 'SHOW'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {items[tabSelectedIndex].children}
        </Grid>
      </Container>
    </>
  );
};
export default Results;
